import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Button,
  Row,
  Col,
  message,
  Spin,
  Descriptions,
  InputNumber,
  Space,
} from 'antd';
import { SaveOutlined, RollbackOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as adminactions from '../../store/actions/AuthActions';

const AttendanceForm = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams(); // mode: 'add', 'edit', 'view'
  const [form] = Form.useForm();
  const organization = useSelector((state) => state.auth.organization);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  // Load employees and shifts
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [employeesRes, shiftsRes] = await Promise.all([
          adminactions.loadEmployeesByOrganization(organization, 1, null, 1000),
          adminactions.loadWorkShifts(1, 1000, organization),
        ]);

        if (employeesRes && employeesRes.results) {
          setEmployees(employeesRes.results);
        }
        if (shiftsRes && shiftsRes.results) {
          setShifts(shiftsRes.results);
        }
      } catch (error) {
        message.error('Failed to load dropdown data');
        console.error('Error loading dropdown data:', error);
      }
    };

    if (organization) {
      loadDropdownData();
    }
  }, [organization]);

  // Load attendance record for edit/view mode
  useEffect(() => {
    const loadAttendance = async () => {
      if ((isEditMode || isViewMode) && id) {
        setLoading(true);
        try {
          const response = await adminactions.loadAttendance(id);
          if (response) {
            setAttendanceData(response);

            // Populate form fields
            form.setFieldsValue({
              employee: response.employee,
              date: response.date ? dayjs(response.date) : null,
              shift: response.shift,
              first_in: response.first_in ? dayjs(response.first_in, 'HH:mm:ss') : null,
              last_out: response.last_out ? dayjs(response.last_out, 'HH:mm:ss') : null,
              status: response.status,
              total_hours_worked: response.total_hours_worked,
              overtime_hours: response.overtime_hours,
              late_arrival_minutes: response.late_arrival_minutes,
              early_departure_minutes: response.early_departure_minutes,
              notes: response.notes,
            });
          }
        } catch (error) {
          message.error('Failed to load attendance record');
          console.error('Error loading attendance:', error);
          navigate('/attendance');
        } finally {
          setLoading(false);
        }
      }
    };

    loadAttendance();
  }, [id, mode]);

  // Handle form submission
  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        employee: values.employee,
        date: values.date.format('YYYY-MM-DD'),
        shift: values.shift,
        first_in: values.first_in ? values.first_in.format('HH:mm:ss') : null,
        last_out: values.last_out ? values.last_out.format('HH:mm:ss') : null,
        status: values.status,
        total_hours_worked: values.total_hours_worked || 0,
        overtime_hours: values.overtime_hours || 0,
        late_arrival_minutes: values.late_arrival_minutes || 0,
        early_departure_minutes: values.early_departure_minutes || 0,
        notes: values.notes || '',
      };

      if (isEditMode) {
        await adminactions.updateAttendance(id, payload);
        message.success('Attendance record updated successfully');
      } else {
        await adminactions.createAttendance(payload);
        message.success('Attendance record created successfully');
      }

      navigate('/attendance');
    } catch (error) {
      message.error(isEditMode ? 'Failed to update attendance' : 'Failed to create attendance');
      console.error('Error saving attendance:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          isViewMode ? 'View Attendance' : isEditMode ? 'Edit Attendance' : 'Add Attendance'
        }
        extra={
          <Button icon={<RollbackOutlined />} onClick={() => navigate('/attendance')}>
            Back
          </Button>
        }
      >
        {isViewMode ? (
          // View Mode - Display as Descriptions
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Employee">
              {attendanceData?.employee_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {attendanceData?.date}
            </Descriptions.Item>
            <Descriptions.Item label="Shift">
              {attendanceData?.shift_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {attendanceData?.status?.toUpperCase()}
            </Descriptions.Item>
            <Descriptions.Item label="Check In">
              {attendanceData?.first_in ? `${attendanceData.date} ${attendanceData.first_in}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Check Out">
              {attendanceData?.last_out ? `${attendanceData.date} ${attendanceData.last_out}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Total Hours Worked">
              {attendanceData?.total_hours_worked || 0} hours
            </Descriptions.Item>
            <Descriptions.Item label="Overtime Hours">
              {attendanceData?.overtime_hours || 0} hours
            </Descriptions.Item>
            <Descriptions.Item label="Late Arrival">
              {attendanceData?.late_arrival_minutes || 0} minutes
            </Descriptions.Item>
            <Descriptions.Item label="Early Departure">
              {attendanceData?.early_departure_minutes || 0} minutes
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {attendanceData?.notes || '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          // Edit/Add Mode - Display as Form
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              status: 'present',
              total_hours_worked: 0,
              overtime_hours: 0,
              late_arrival_minutes: 0,
              early_departure_minutes: 0,
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="employee"
                  label="Employee"
                  rules={[{ required: true, message: 'Please select an employee' }]}
                >
                  <Select
                    placeholder="Select Employee"
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {employees.map((emp) => (
                      <Select.Option key={emp.id} value={emp.id}>
                        {emp.user?.first_name} {emp.user?.last_name} ({emp.employee_id || 'N/A'})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="date"
                  label="Date"
                  rules={[{ required: true, message: 'Please select a date' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="shift" label="Shift">
                  <Select placeholder="Select Shift" allowClear>
                    {shifts.map((shift) => (
                      <Select.Option key={shift.id} value={shift.id}>
                        {shift.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select a status' }]}
                >
                  <Select placeholder="Select Status">
                    <Select.Option value="present">Present</Select.Option>
                    <Select.Option value="absent">Absent</Select.Option>
                    <Select.Option value="leave">Leave</Select.Option>
                    <Select.Option value="holiday">Holiday</Select.Option>
                    <Select.Option value="partial">Partial Day</Select.Option>
                    <Select.Option value="late">Late Arrival</Select.Option>
                    <Select.Option value="early">Early Departure</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="first_in" label="Check In Time">
                  <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="last_out" label="Check Out Time">
                  <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="total_hours_worked" label="Total Hours Worked">
                  <InputNumber
                    min={0}
                    step={0.25}
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="overtime_hours" label="Overtime Hours">
                  <InputNumber
                    min={0}
                    step={0.25}
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="late_arrival_minutes" label="Late Arrival (Minutes)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="early_departure_minutes" label="Early Departure (Minutes)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={3} placeholder="Enter any notes..." />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
                <Button onClick={() => navigate('/attendance')}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default AttendanceForm;
